/**
 * How a new deployment reaches a device that has been running for weeks.
 *
 * READ THIS BEFORE CHANGING THE VITE PWA CONFIG — the two have to agree.
 *
 * WHAT WAS WRONG. The build used `registerType: 'autoUpdate'`, which generates
 * a service worker calling `skipWaiting()` + `clientsClaim()` +
 * `cleanupOutdatedCaches()`. Measured consequence, not a theory: on every
 * deploy the new worker activated immediately, claimed the page that was
 * already open, and DELETED the old precache — while that page was still
 * running the old entry bundle and would lazy-import old chunk hashes. Those
 * hashes are gone from the cache, and the static host answers the request with
 * index.html and a 200 rather than a 404, so the next navigation inside the
 * running app died with "Failed to fetch dynamically imported module". Every
 * deploy did this to every open session.
 *
 * And the other half: registration happened once, on window load, with no
 * periodic check. A kiosk sitting on one route makes no in-scope navigations,
 * so nothing ever prompted the browser to re-fetch sw.js. It could run an
 * arbitrarily old shell indefinitely — and the reload that finally picked up
 * the new one was the moment the crash above fired.
 *
 * WHAT HAPPENS NOW.
 *
 *   1. The new worker WAITS. Nothing is swapped and nothing is deleted while a
 *      page is using it. `registerType: 'prompt'` in vite.config.ts is what
 *      turns skipWaiting off; this module is the "prompt".
 *   2. We ask for an update every UPDATE_EVERY, so an unattended screen learns
 *      about deploys without anyone touching it.
 *   3. When one is waiting we apply it ONLY WHEN THE DEVICE IS IDLE, and
 *      applying it means activating the new worker and reloading — one clean
 *      transition, no half-old-half-new page.
 *
 * WHAT "IDLE" MEANS, AND WHY IT IS THAT. A kiosk is unattended and reboots, so
 * it must update itself; but reloading it mid-check-in would wipe what a
 * visitor has typed, and the capture queue protects submitted work, not
 * keystrokes. So: nobody has touched the screen for QUIET_FOR, and no text
 * field currently holds anything. Both conditions, because either alone is
 * wrong — someone reading a badge number has not touched anything for a
 * minute, and someone typing slowly has an empty field between keystrokes.
 *
 * The unsent queue is deliberately NOT a condition. A reload never loses
 * queued work — it is in localStorage, which is the entire point of it — and
 * gating on an empty queue would wedge updates on exactly the device that most
 * needs them: one that has been offline for a week with captures waiting.
 */

import { getState } from '@/services/pendingQueue';

/** How often to ask the browser to look for a new worker. Hourly: frequent
 *  enough that a kiosk is never more than a shift behind, rare enough to be
 *  invisible. The request is conditional and costs a few hundred bytes. */
export const UPDATE_EVERY = 60 * 60 * 1000;

/** How long the screen must be untouched before a reload is allowed. */
export const QUIET_FOR = 60 * 1000;

/** How often to re-check whether the moment has become a good one. */
const IDLE_POLL = 15 * 1000;

let lastInteraction = Date.now();

function markInteraction(): void {
  lastInteraction = Date.now();
}

/** Is anything typed into the page right now?
 *
 *  Checked across every text-ish control rather than the focused one: a
 *  visitor who filled three kiosk fields and paused to find their host's name
 *  has an unfocused form full of work, and reloading it away would be the same
 *  loss this whole area of the codebase exists to prevent. Password fields are
 *  included; a half-typed login is work too. */
export function hasUnsubmittedInput(doc: Document = document): boolean {
  const fields = doc.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    'input:not([type=hidden]):not([type=checkbox]):not([type=radio]):not([type=submit]):not([type=button]), textarea',
  );
  for (const field of fields) {
    // A search box preloaded with a placeholder value is not somebody's work,
    // but a readOnly or disabled field cannot be either — skip both.
    if (field.readOnly || field.disabled) continue;
    if ((field.value ?? '').trim() !== '') return true;
  }
  return false;
}

/** Is this a safe moment to throw the page away and load a new one? */
export function isIdle(now: number = Date.now()): boolean {
  if (now - lastInteraction < QUIET_FOR) return false;
  if (hasUnsubmittedInput()) return false;
  return true;
}

/** Purely for the log line — the queue survives a reload either way, and this
 *  is never allowed to block one. */
function queueDepth(): number {
  try {
    const { waiting, blocked } = getState();
    return waiting.length + blocked.length;
  } catch {
    return 0;
  }
}

/**
 * Register the worker and manage its lifecycle. Returns a teardown.
 *
 * Called from main.tsx. The registration is here and NOT injected into
 * index.html by the plugin (`injectRegister: null`), so there is exactly one
 * place that registers a worker and exactly one policy for updating it.
 */
export function initServiceWorker(): () => void {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return () => {};
  }

  const events: Array<keyof WindowEventMap> = [
    'pointerdown', 'keydown', 'touchstart', 'wheel',
  ];
  events.forEach((e) => window.addEventListener(e, markInteraction, { passive: true }));

  let updateTimer = 0;
  let idleTimer = 0;
  let applying = false;

  void (async () => {
    let registerSW: typeof import('virtual:pwa-register').registerSW;
    try {
      ({ registerSW } = await import('virtual:pwa-register'));
    } catch {
      // No worker in this build (dev, or the plugin disabled). Nothing to do,
      // and nothing about the app depends on it being there.
      return;
    }

    const applyWhenIdle = (apply: () => void) => {
      if (applying) return;
      applying = true;
      const tick = () => {
        if (isIdle()) {
          window.clearInterval(idleTimer);
          console.info(
            `[sw] applying a waiting update; ${queueDepth()} unsent item(s) ` +
            'stay on this device across the reload',
          );
          apply();                     // skipWaiting + reload, together
          return;
        }
      };
      idleTimer = window.setInterval(tick, IDLE_POLL);
      tick();
    };

    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        // A new worker is installed and WAITING. Nothing has been swapped and
        // the old precache is intact, so the page keeps working exactly as it
        // did until we choose the moment.
        applyWhenIdle(() => void updateSW(true));
      },
      onRegisteredSW(_url, registration) {
        if (!registration) return;
        // The check an unattended screen would otherwise never make.
        updateTimer = window.setInterval(() => {
          if (navigator.onLine === false) return;   // pointless, and noisy in the log
          void registration.update().catch(() => {
            /* offline, or the host blinked; the next tick tries again */
          });
        }, UPDATE_EVERY);
      },
      onRegisterError(error) {
        console.warn('[sw] registration failed; the app still works online', error);
      },
    });
  })();

  return () => {
    events.forEach((e) => window.removeEventListener(e, markInteraction));
    window.clearInterval(updateTimer);
    window.clearInterval(idleTimer);
  };
}
